"use client";

import Image from "next/image";
import React, { useState } from "react";

// ─── Loom embed helper ────────────────────────────────────────────────────────

function LoomEmbed({ url }: { url: string }) {
  const [isLoading, setIsLoading] = useState(true);
  const match = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (!match) return null;
  const embedUrl = `https://www.loom.com/embed/${match[1]}`;

  return (
    <div className="my-6 rounded-xl overflow-hidden border border-gray-100 shadow-sm">
      <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50">
            <div className="w-6 h-6 rounded-full border-2 border-gray-200 border-t-gray-500 animate-spin" />
          </div>
        )}
        <iframe
          src={embedUrl}
          allowFullScreen
          className="absolute inset-0 w-full h-full"
          title="Video tutorial"
          onLoad={() => setIsLoading(false)}
        />
      </div>
    </div>
  );
}

function LoadingImage({ src }: { src: string }) {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div className="relative rounded-xl overflow-hidden border border-gray-100 shadow-sm min-h-[180px]">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50">
          <div className="w-6 h-6 rounded-full border-2 border-gray-200 border-t-gray-500 animate-spin" />
        </div>
      )}
      <Image
        src={src}
        alt="Reference image"
        width={800}
        height={500}
        className="w-full h-auto"
        sizes="(max-width: 768px) 100vw, 800px"
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
}

// ─── Article body renderer ────────────────────────────────────────────────────

interface ArticleContentProps {
  answer: string;
  images?: string[];
}

export function ArticleContent({ answer, images }: ArticleContentProps) {
  const lines = answer.split("\n");
  const loomUrls: string[] = [];
  const contentLines: string[] = [];

  for (const line of lines) {
    if (line.match(/https?:\/\/(www\.)?loom\.com\/share\//)) {
      loomUrls.push(line.trim());
    } else {
      contentLines.push(line);
    }
  }

  return (
    <div className="space-y-4">
      {/* Text content */}
      <div className="text-[15px] text-gray-700 leading-[1.8]">
        {contentLines.map((line, i) => {
          const trimmed = line.trim();

          if (!trimmed) {
            return <div key={i} className="h-3" />;
          }

          // Bullet/dash list items
          if (trimmed.startsWith("–") || trimmed.startsWith("-")) {
            return (
              <div key={i} className="flex gap-2.5 pl-1 py-1">
                <span className="text-purple-400 shrink-0 mt-px">–</span>
                <span className="text-gray-600">
                  {trimmed.replace(/^[–-]\s*/, "")}
                </span>
              </div>
            );
          }

          // Subheading detection
          const isSubheading =
            trimmed.length < 80 &&
            !trimmed.endsWith(".") &&
            !trimmed.endsWith(",") &&
            !trimmed.startsWith("–") &&
            !trimmed.startsWith("-") &&
            i > 0 &&
            trimmed !== contentLines[0]?.trim() &&
            (contentLines[i + 1]?.trim().startsWith("–") ||
              contentLines[i + 1]?.trim().startsWith("-") ||
              (trimmed.length < 50 &&
                !trimmed.includes("£") &&
                !trimmed.includes("@")));

          if (isSubheading) {
            return (
              <h4
                key={i}
                className="text-[15px] font-normal text-gray-900 mt-6 mb-1.5 font-satoshi"
              >
                {trimmed}
              </h4>
            );
          }

          return (
            <p key={i} className="my-1.5 text-gray-600">
              {trimmed}
            </p>
          );
        })}
      </div>

      {/* Images */}
      {images && images.length > 0 && (
        <div className="space-y-4 mt-8">
          {images.map((src, i) => (
            <LoadingImage key={i} src={src} />
          ))}
        </div>
      )}

      {/* Loom video embeds */}
      {loomUrls.map((url, i) => (
        <LoomEmbed key={i} url={url} />
      ))}
    </div>
  );
}
